import { inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly toastrService = inject(ToastrService);

  // Show the reason an API call was refused.
  //
  // A deliberate BadRequest carries a message explaining WHAT the user has to do about it
  // ("that roll is already on a transfer"), and without this it goes only to the network tab.
  // The fallback is used when the failure carries nothing useful - a 500, or a dropped
  // connection - where a raw stack trace or empty string would be worse than useless.
  //
  // ⚠️ The API's message is composed in C# and is therefore ENGLISH. `fallback` is localized
  // and is what a non-English user sees for anything unexpected; a deliberate validation
  // message still arrives in English. Localising those needs the API to return codes rather
  // than prose - a deliberate piece of work, not something this helper can paper over.
  apiFail(error: any, fallback: string) {
    const message = typeof error?.error === 'string' && error.error.trim() ? error.error.trim() : null;

    return this.fail(message ?? fallback);
  }

  saveSuccess(name: string) {
    return this.success($localize`${name} saved successfully`);
  }

  saveFail(name: string) {
    return this.fail($localize`${name} save failed`);
  }

  emailSuccess(name: string) {
    return this.success($localize`${name} email sent successfully`);
  }

  emailFail(name: string) {
    return this.fail($localize`Failed to send ${name} email`);
  }

  uploadSuccess(name: string) {
    return this.success($localize`${name} uploaded successfully`);
  }

  uploadFail(name: string) {
    return this.fail($localize`Failed to upload ${name} file`);
  }

  success(message: string) {
    return this.toastrService.success(message);
  }

  /**
   * Something went through, but the user has to act on it.
   *
   * Distinct from `success` on purpose: an amber toast for "this worked, but..." reads as needing
   * attention, where the same words in a green box read as "all fine" and get dismissed.
   */
  warning(message: string) {
    return this.toastrService.warning(message);
  }

  fail(message: string) {
    return this.toastrService.error(message);
  }
}
